import {useEffect} from 'react';
import {useBlocker} from 'react-router-dom';

export interface UseUnsavedChangesGuardResult {
  /** True while an in-app navigation attempt is being held pending confirmation. */
  isBlocked: boolean;
  /** Proceeds with the blocked navigation, discarding unsaved changes. */
  confirmLeave: () => void;
  /** Cancels the blocked navigation, staying on the current page. */
  cancelLeave: () => void;
}

/**
 * Guards against navigating away from a page with unsaved changes.
 *
 * Blocks in-app (client-side router) navigation via `useBlocker` — requires
 * the app to be using a data router (`createBrowserRouter`/`RouterProvider`),
 * not `<BrowserRouter>`. Also guards full page unload/refresh/tab-close via
 * `beforeunload`, which the router cannot intercept.
 *
 * This hook only exposes state (`shared/` may not import UI components from
 * `design/`, per the app's layering rules) — consumers render
 * their own confirm dialog (e.g. `design/molecules/ConfirmDialog`) driven by
 * `isBlocked`, wiring its confirm/cancel actions to `confirmLeave`/
 * `cancelLeave`.
 *
 * Not wired into any page yet — pages opt in by calling this hook with a
 * `dirty` flag.
 */
export const useUnsavedChangesGuard = (
  dirty: boolean
): UseUnsavedChangesGuardResult => {
  const blocker = useBlocker(
    ({currentLocation, nextLocation}) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (!dirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers require returnValue to be set to trigger the prompt.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dirty]);

  const isBlocked = blocker.state === 'blocked';

  const confirmLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const cancelLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  return {isBlocked, confirmLeave, cancelLeave};
};
